/**
 * 再生せずに音源を分類する（先頭だけ取得してデコードする）。
 *
 * サムネイルの配色は「実際に鳴っている音」だけを根拠にする方針なので、
 * 一覧に並んだ時点で分類が済んでいてほしい。かといって全長をダウンロード
 * するのは論外なので、Range で先頭だけ取り、decodeAudioData に通す。
 *
 * MP3/AAC は先頭から順に復号できるので、切り詰めたバイト列でも先頭部分の
 * PCM が得られる（末尾の不完全なフレームは実装が落としてくれる）。
 * 実測では 400KB / 128kbps ≒ 12 秒分が 1 秒強で取れた。分類には十分。
 *
 * ■ 設計上の注意
 * - 一覧では十数枚が同時に可視になるので、同時実行数を絞る。絞らないと
 *   本命であるサムネイル画像や音声本体の取得を圧迫する。
 * - 結果は spectrumCategory のストア経由で localStorage に載る。二度目の
 *   訪問では取得も解析も走らない。
 * - CORS が通らないオリジンでは fetch 自体が失敗する。その場合は黙って
 *   諦め、再生時の AnalyserNode 経由の推定に委ねる。
 */

import { createFftPlan, hannWindow } from './fft';
import { createFeatureExtractor } from './spectrumFeatures';
import {
  createSpectrumEstimator,
  FRAME_RATE,
  getSpectrumCategory,
  setSpectrumCategory,
  SpectrumEstimate,
  SpectrumStats,
} from './spectrumCategory';

/** 取得する先頭バイト数。128kbps なら約 25 秒、320kbps でも約 10 秒 */
const PREFIX_BYTES = 400_000;
/** 解析窓。44.1kHz で 1 ビン ≒ 21Hz */
const WINDOW_SIZE = 2048;
/** 同時に走らせる解析の数 */
const MAX_CONCURRENCY = 2;
/** これを超える長さは解析しない（分類には前半で足りる） */
const MAX_ANALYSIS_SEC = 30;

type Category = SpectrumEstimate['category'];

const inFlight = new Map<string, Promise<Category | null>>();
/** 失敗した URL。同じものを何度も叩かない */
const failed = new Set<string>();

let running = 0;
const queue: Array<() => void> = [];

const acquireSlot = (): Promise<void> =>
  new Promise(resolve => {
    if (running < MAX_CONCURRENCY) {
      running += 1;
      resolve();
      return;
    }
    queue.push(() => {
      running += 1;
      resolve();
    });
  });

const releaseSlot = (): void => {
  running -= 1;
  queue.shift()?.();
};

const cacheKey = (url: string): string => url.split('?')[0];

/**
 * 音源の先頭を取得して分類する。
 *
 * 同じ音源への同時呼び出しは 1 本にまとめられ、判定済みのものは即座に返る。
 * 判定できなかった場合（CORS 不可、短すぎる、確信が足りない）は null。
 * 結果は spectrumCategory ストアにも書かれるので、購読側は戻り値を待たずに
 * 再描画される。
 */
export function analyseAudioPrefix(url: string): Promise<Category | null> {
  const key = cacheKey(url);

  const cached = getSpectrumCategory(url);
  if (cached) return Promise.resolve(cached);
  if (failed.has(key)) return Promise.resolve(null);

  const existing = inFlight.get(key);
  if (existing) return existing;

  const task = run(url)
    .then(category => {
      if (category) setSpectrumCategory(url, category);
      else failed.add(key);
      return category;
    })
    .catch(() => {
      failed.add(key);
      return null;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, task);
  return task;
}

async function run(url: string): Promise<Category | null> {
  await acquireSlot();
  try {
    const prefix = await fetchPrefix(url);
    if (!prefix) return null;

    // decodeAudioData は渡した ArrayBuffer を切り離すので、長さは先に控える
    const fetchedBytes = prefix.buffer.byteLength;
    const audio = await decodePrefix(prefix.buffer);
    if (!audio) return null;

    const limit = Math.min(audio.length, Math.floor(audio.sampleRate * MAX_ANALYSIS_SEC));
    if (limit < WINDOW_SIZE * 4) return null;

    const analysis = analyseSamples(
      downmix(audio, limit),
      audio.sampleRate,
      estimateDuration(audio, fetchedBytes, prefix.totalBytes),
    );
    return analysis?.category ?? null;
  } finally {
    releaseSlot();
  }
}

interface Prefix {
  buffer: ArrayBuffer;
  /** 音源全体のバイト数。Content-Range から判らなければ null */
  totalBytes: number | null;
}

async function fetchPrefix(url: string): Promise<Prefix | null> {
  try {
    const response = await fetch(url, { headers: { Range: `bytes=0-${PREFIX_BYTES - 1}` } });
    if (!response.ok) return null;

    // "bytes 0-399999/5242880" の分母が全体長。Range 非対応で 200 が返った
    // 場合は全体が来ているので、受け取った長さがそのまま全体長になる
    const range = response.headers.get('Content-Range');
    const total = range ? Number(range.split('/')[1]) : NaN;
    const buffer = await response.arrayBuffer();

    return {
      buffer,
      totalBytes: Number.isFinite(total) ? total : response.status === 200 ? buffer.byteLength : null,
    };
  } catch {
    // CORS 不可・ネットワーク断。再生時の解析に委ねる
    return null;
  }
}

/**
 * 音源全体の長さ（秒）。先頭しか取っていないので、取れた分の再生時間を
 * バイト数の比で外挿する。可変ビットレートでは誤差が出るが、尺は「効果音か
 * 楽曲か」の粗い判断にしか使わないのでこの精度で足りる。
 */
function estimateDuration(
  audio: AudioBuffer,
  fetchedBytes: number,
  totalBytes: number | null,
): number | null {
  const decoded = audio.length / audio.sampleRate;
  if (totalBytes === null || fetchedBytes <= 0) return null;
  // 全体が取れているならデコードできた長さがそのまま尺
  if (totalBytes <= fetchedBytes) return decoded;
  return decoded * (totalBytes / fetchedBytes);
}

async function decodePrefix(buffer: ArrayBuffer): Promise<AudioBuffer | null> {
  // OfflineAudioContext なら自動再生ポリシーに関係なくデコードできる。
  // sampleRate は decodeAudioData の出力には影響しない（元の値が使われる）
  const context = new OfflineAudioContext(1, 1, 44100);
  try {
    return await context.decodeAudioData(buffer);
  } catch {
    // Range で切った末尾が壊れている、あるいは未対応コンテナ
    return null;
  }
}

export interface SampleAnalysis {
  /** 判定結果。確信度が足りなければ null */
  category: Category | null;
  /** 判定に使った中間値。しきい値の追い込み用 */
  stats: SpectrumStats;
}

/**
 * モノラル PCM を分類する。解析に足りる長さが無ければ null。
 *
 * デコード結果からの呼び出しが本筋だが、合成波形での検証にも使えるよう
 * AudioBuffer ではなく素の配列を受ける。
 */
export function analyseSamples(
  mono: Float32Array,
  sampleRate: number,
  durationSec: number | null = null,
): SampleAnalysis | null {
  // 分類器の時間方向の指標は FRAME_RATE を前提に較正してあるので、
  // ホップ幅をそのレートに合わせる
  const hop = Math.max(1, Math.round(sampleRate / FRAME_RATE));
  const limit = mono.length;
  if (limit < WINDOW_SIZE * 4) return null;

  const plan = createFftPlan(WINDOW_SIZE);
  const window = hannWindow(WINDOW_SIZE);
  const extract = createFeatureExtractor(sampleRate, plan.binCount);
  const estimator = createSpectrumEstimator(durationSec);

  const frame = new Float32Array(WINDOW_SIZE);
  const magnitude = new Float32Array(plan.binCount);
  const dtSec = hop / sampleRate;

  for (let start = 0; start + WINDOW_SIZE <= limit; start += hop) {
    for (let i = 0; i < WINDOW_SIZE; i++) {
      frame[i] = mono[start + i] * window[i];
    }
    plan.forward(frame, magnitude);
    estimator.push(extract(magnitude, dtSec));
  }

  return { category: estimator.estimate()?.category ?? null, stats: estimator.inspect() };
}

/** チャンネルを平均してモノラルにする */
function downmix(audio: AudioBuffer, limit: number): Float32Array {
  const channels = audio.numberOfChannels;
  const mono = audio.getChannelData(0).slice(0, limit);
  if (channels === 1) return mono;

  for (let c = 1; c < channels; c++) {
    const data = audio.getChannelData(c);
    for (let i = 0; i < limit; i++) mono[i] += data[i];
  }
  for (let i = 0; i < limit; i++) mono[i] /= channels;
  return mono;
}
